/**
 * Smart barge-in for OpenAI Realtime voice agents (phone + browser).
 *
 * With plain server VAD every sound on the line — a TV, a door, someone in the
 * room, a cough — cancels the agent mid-sentence and triggers a fresh reply,
 * so it stops and repeats itself. Sessions therefore run with
 * `interrupt_response: false, create_response: false` and this gate decides:
 *
 *   - every caller turn is held until its transcript arrives (≤ 1.5 s when
 *     the agent is silent): noise, phantom lines ("Thank you.", subtitles
 *     credits) and text in a script the call can't be in are deleted, so the
 *     model never answers words it imagined from a TV or the line;
 *   - agent talking → real words interrupt (cancel + new reply), "haan / ok /
 *     hmm" acknowledgements and noise do not.
 *
 * Pure logic over an IO adapter, shared by the worker (phone) and the web
 * client (WebRTC).
 */
import { romanizeIndic } from './romanize.js';
const BACKCHANNEL = new Set([
    'hmm', 'hm', 'hmmm', 'mm', 'mmm', 'mhm', 'uh', 'um', 'uhh', 'umm', 'ah', 'aha', 'oh', 'ohh',
    'ok', 'okay', 'okey', 'yes', 'yeah', 'yep', 'ya', 'yah', 'right', 'sure', 'fine', 'alright', 'huh',
    'haan', 'han', 'haa', 'haaan', 'ha', 'ji', 'jee', 'accha', 'acha', 'achha', 'achcha', 'theek', 'thik', 'hai', 'ho',
    'हाँ', 'हां', 'हा', 'जी', 'अच्छा', 'ठीक', 'है', 'हम्म', 'हम', 'ओके', 'हाँजी', 'हांजी',
]);
// Whisper-family "hallucinations" on silence / line noise.
const PHANTOMS = new Set([
    'you', 'thank you', 'thanks', 'thanks for watching', 'thank you for watching', 'bye', 'bye bye',
    'please subscribe', 'subtitles by the amaraorg community', 'so', 'the', 'a', 'i', 'oh', 'uh', 'um',
    'thank you very much', 'thanks for listening', 'see you next time', 'like and subscribe',
    'ご視聴ありがとうございました', '字幕', 'продолжение следует', 'субтитры', 'спасибо за просмотр',
    '请不吝点赞 订阅 转发 打赏支持明镜与点点栏目', 'mbc 뉴스', '감사합니다', 'धन्यवाद',
]);
/** Scripts a call in this language can produce (others = noise). null = don't check. */
export function expectedScripts(language) {
    const l = (language || '').toLowerCase();
    if (!l || l === 'auto' || l === 'en' || l === 'hi' || l === 'hinglish')
        return /[A-Za-z\u0900-\u097F]/;
    if (['bn', 'pa', 'gu', 'or', 'ta', 'te', 'kn', 'ml', 'mr', 'as', 'ne'].includes(l))
        return /[A-Za-z\u0900-\u0D7F]/;
    if (['es', 'fr', 'de', 'it', 'pt', 'nl', 'id', 'ms', 'tl', 'sw', 'tr', 'pl', 'ro', 'vi'].includes(l))
        return /[A-Za-zÀ-ɏ]/;
    return null;
}
// Non-speech sounds the transcriber writes out as words ("khansi" = cough).
const SOUND_WORDS = new Set([
    'cough', 'coughs', 'coughing', 'khansi', 'khaansi', 'khasi', 'khaasi', 'khans', 'sneeze', 'sneezes', 'sneezing', 'chheenk', 'chhink', 'chink',
    'laugh', 'laughs', 'laughing', 'laughter', 'haha', 'hahaha', 'hehe', 'sigh', 'sighs', 'sighing', 'breath', 'breathing', 'breathes', 'gasp',
    'music', 'noise', 'noises', 'static', 'silence', 'inaudible', 'unintelligible', 'applause', 'ahem', 'beep', 'beeps', 'ringing', 'ring', 'knock', 'knocking',
    'background', 'clears', 'clearing', 'throat', 'crying', 'cries', 'sniff', 'sniffs', 'yawn', 'yawns', 'click', 'clicks', 'typing', 'footsteps',
    'blank_audio', 'speaking', 'foreign', 'language', 'the', 'and', 'a', 'of', 'in', 'on',
]);
const SOUND_CORE = new Set([...SOUND_WORDS].filter((w) => !['background', 'speaking', 'foreign', 'language', 'the', 'and', 'a', 'of', 'in', 'on', 'clears', 'clearing', 'throat'].includes(w)));
// What an assistant (or a YouTube outro) says — the transcriber invents these
// from noise; a caller does not say "how may I help you" to the agent.
const ASSISTANT_LIKE = [
    /\b(main|mai|mein|me)\s+(aapki|apki|aap ki|ap ki|aapka|apka)\s+(kaise|kya|aur|kis tarah)?\s*(madad|sahayata|help)\s+(kar|kr)\s+(sakta|sakti|sakte)/,
    /\bkya\s+(main|mai|mein|me)\s+(aapki|apki)?\s*(kuchh|kuch)?\s*(aur)?\s*(madad|sahayata|help)/,
    /\bkripya\s+(apna|aapna)\s+(nam|naam)\s+(bataen|bataye|batayen|bataiye|batayein)/,
    /\b(subscribe|like|share)\s+(karen|kare|karein|kijiye)\b/,
    /\bhow\s+(can|may)\s+i\s+(help|assist)\s+you\b/,
    /\b(is there )?anything else i can (help|do)\b/,
    /\bthanks?\s+(you\s+)?for\s+(calling|watching|listening)\b/,
    /\bplease\s+(like|subscribe)\b/,
    /\b(dhanyavad|dhanyawad|dhanyavaad)\s+(dekhne|sunne)\b/,
];
export function normaliseLine(text) {
    return text.toLowerCase().replace(/[^\p{L}\p{N}\p{M}\s]/gu, ' ').replace(/\s+/g, ' ').trim();
}
/**
 * Classify a caller line heard while the agent was talking. `botText` = what
 * the agent was saying (a line that merely repeats it is echo, not the caller).
 */
export function classifyCallerLine(text, botText = '', scripts = null) {
    // [coughing], (laughs), *sighs*, ♪ … — sound descriptions, not words.
    if (/^\s*[[(*♪♫].*[\])*♪♫]\s*$/u.test(text))
        return 'noise';
    const t = normaliseLine(text);
    if (!t || PHANTOMS.has(t))
        return 'noise';
    const latin = normaliseLine(romanizeIndic(text));
    if (PHANTOMS.has(latin))
        return 'noise';
    const latinWords = latin.split(' ').filter(Boolean);
    if (latinWords.length && latinWords.length <= 4 && latinWords.every((w) => SOUND_WORDS.has(w)) && latinWords.some((w) => SOUND_CORE.has(w)))
        return 'noise';
    if (ASSISTANT_LIKE.some((re) => re.test(latin)))
        return 'noise';
    // e.g. Korean / Russian / Japanese on a Hindi-English call: invented from noise.
    if (scripts && /\p{L}/u.test(t)) {
        const letters = [...t].filter((c) => /\p{L}/u.test(c));
        const ok = letters.filter((c) => scripts.test(c)).length;
        if (ok / letters.length < 0.5)
            return 'noise';
    }
    const words = t.split(' ');
    if (words.length === 1 && [...words[0]].length < 2)
        return 'noise';
    const bot = normaliseLine(botText);
    if (bot && t.length >= 8 && bot.includes(t))
        return 'noise';
    // Overlapping turn that mostly repeats what the agent was saying = its own echo.
    if (bot && latinWords.length >= 6) {
        const botWords = new Set(normaliseLine(romanizeIndic(botText)).split(' '));
        const shared = latinWords.filter((w) => botWords.has(w)).length;
        if (shared / latinWords.length >= 0.75)
            return 'noise';
    }
    if (words.length <= 3 && words.every((w) => BACKCHANNEL.has(w)))
        return 'backchannel';
    return 'speech';
}
/** An acknowledgement this close to the end of the agent's turn is an answer. */
const TAIL_MS = 1500;
/** Give up waiting for a transcript after this long (agent talking / silent). */
const TRANSCRIPT_WAIT_MS = 4000;
const QUIET_WAIT_MS = 1500;
export class BargeInGate {
    io;
    now;
    responding = false;
    held = new Map();
    /** Turns that started while the agent was talking (only those can be echo). */
    overlapped = new Set();
    ackAt = 0;
    followTimer = null;
    botText = '';
    ducked = false;
    scripts = expectedScripts('auto');
    /** Replies the agent finished (response.done) — lets a hang-up wait for the goodbye. */
    responsesDone = 0;
    /** How many times the caller said real words. */
    callerSpeechCount = 0;
    constructor(io, now = Date.now) {
        this.io = io;
        this.now = now;
    }
    /** The call's language (snapshot agent.language) — limits accepted scripts. */
    setLanguage(language) {
        this.scripts = expectedScripts(language);
    }
    get botSpeaking() {
        return this.responding || this.io.isPlaying();
    }
    /** Every response.create we send goes through here. */
    requestResponse() {
        this.responding = true;
        this.io.respond();
    }
    responseCreated() {
        this.responding = true;
        this.botText = '';
    }
    botDelta(delta) {
        this.botText += delta;
    }
    responseDone() {
        this.responding = false;
        this.responsesDone++;
        this.armFollowUp();
    }
    speechStarted() {
        if (this.botSpeaking)
            this.duck(true);
    }
    speechStopped() {
        // keep ducked until the transcript decides
    }
    /** input_audio_buffer.committed — a caller turn ended. */
    committed(itemId) {
        // Decide once we know what was said — also when the agent is silent, or a
        // TV / other voices get an answer to words the model imagined.
        const wait = this.botSpeaking ? TRANSCRIPT_WAIT_MS : QUIET_WAIT_MS;
        if (this.botSpeaking)
            this.overlapped.add(itemId);
        this.held.set(itemId, setTimeout(() => this.transcript(itemId, null), wait));
    }
    /** Caller transcript (null = transcription failed / timed out). */
    transcript(itemId, text) {
        const timer = this.held.get(itemId);
        if (timer === undefined)
            return;
        clearTimeout(timer);
        this.held.delete(itemId);
        const echoOf = this.overlapped.delete(itemId) ? this.botText : '';
        const kind = text === null ? 'backchannel' : classifyCallerLine(text, echoOf, this.scripts);
        if (this.held.size === 0)
            this.duck(false);
        if (kind === 'speech') {
            this.callerSpeechCount++;
            this.io.onCallerSpeech?.();
            if (this.botSpeaking)
                this.io.interrupt();
            this.responding = false;
            this.ackAt = 0;
            this.requestResponse();
            return;
        }
        if (kind === 'noise') {
            this.io.drop(itemId);
            return;
        }
        // Acknowledgement: keep it in the history; answer it only if the agent has
        // already finished (or was about to) — otherwise it was just "hmm, go on".
        if (!this.botSpeaking) {
            this.requestResponse();
            return;
        }
        this.ackAt = this.now();
        this.armFollowUp();
    }
    /**
     * After end_call: resolve true once the agent's goodbye has been spoken
     * (a reply finished and playback stopped), false if the caller said real
     * words meanwhile — then keep the call going. Gives up (true) after maxMs.
     */
    waitForGoodbye(maxMs = 12_000) {
        const startedAt = this.now();
        const doneAtStart = this.responsesDone;
        const speechAtStart = this.callerSpeechCount;
        let quietSince = 0;
        return new Promise((resolve) => {
            const t = setInterval(() => {
                const now = this.now();
                if (this.callerSpeechCount > speechAtStart) {
                    clearInterval(t);
                    resolve(false);
                    return;
                }
                if (now - startedAt > maxMs) {
                    clearInterval(t);
                    resolve(true);
                    return;
                }
                const quiet = !this.botSpeaking && this.held.size === 0 && this.responsesDone > doneAtStart;
                if (!quiet) {
                    quietSince = 0;
                    return;
                }
                if (!quietSince)
                    quietSince = now;
                // A short pause after the goodbye so the last word isn't clipped.
                if (now - quietSince >= 700) {
                    clearInterval(t);
                    resolve(true);
                }
            }, 100);
        });
    }
    dispose() {
        for (const t of this.held.values())
            clearTimeout(t);
        this.held.clear();
        this.overlapped.clear();
        if (this.followTimer)
            clearInterval(this.followTimer);
        this.followTimer = null;
    }
    armFollowUp() {
        if (!this.ackAt || this.followTimer)
            return;
        this.followTimer = setInterval(() => {
            if (this.botSpeaking)
                return;
            if (this.followTimer)
                clearInterval(this.followTimer);
            this.followTimer = null;
            const at = this.ackAt;
            this.ackAt = 0;
            if (at && this.now() - at <= TAIL_MS && this.held.size === 0)
                this.requestResponse();
        }, 150);
    }
    duck(on) {
        if (this.ducked === on)
            return;
        this.ducked = on;
        this.io.duck?.(on);
    }
}
//# sourceMappingURL=voice-barge-in.js.map